import type { AdditionalProductsMetadataV2 } from "@credit-acceptance/step6-bff-client";

export interface VscAccess {
  /** Header renders as a link opening the P3 popup (spec D1: TPAP and the tab not read-only). */
  readonly headerLink: boolean;
  /** Vendor is a dropdown (spec 6.3) rather than disabled text. */
  readonly vendorDropdown: boolean;
  readonly vendorEnabled: boolean;
  readonly certificate: boolean;
  readonly sellingPrice: boolean;
  readonly terms: boolean;
  readonly unlimited: boolean;
  readonly effectiveDate: boolean;
  readonly formNumber: boolean;
  /** Any field is typeable, i.e. the card contributes to the save. */
  readonly anyEditable: boolean;
}

export interface GapAccess {
  readonly headerLink: boolean;
  readonly vendorDropdown: boolean;
  readonly vendorEnabled: boolean;
  readonly addendumPrefix: boolean;
  readonly addendumNumber: boolean;
  readonly sellingPrice: boolean;
  readonly term: boolean;
  readonly amountFinanced: boolean;
  readonly effectiveDate: boolean;
  readonly formNumber: boolean;
  readonly anyEditable: boolean;
}

/**
 * recap's `gatingDegraded` — the 939 client exposes it as `flagsUnreliable`, but a BFF still serving the
 * older name must lock the card too, so both are read.
 * @param metadata `additionalProducts.metadata`.
 * @returns {boolean} Whether the flags can't be trusted.
 */
function isGatingDegraded(metadata: AdditionalProductsMetadataV2 | undefined): boolean {
  const { gatingDegraded } = (metadata ?? {}) as { gatingDegraded?: boolean };
  return metadata?.flagsUnreliable === true || gatingDegraded === true;
}

/**
 * Whether the flags are readable and describe a non-pre-approval deal (only an explicit `preAppDeal: false` counts).
 * @param metadata `additionalProducts.metadata`.
 * @returns {boolean} Whether the flags can be acted on.
 */
function isFlagsTrusted(metadata: AdditionalProductsMetadataV2 | undefined): boolean {
  return metadata?.preAppDeal === false && !isGatingDegraded(metadata);
}

/**
 * Whether the flags permit any edit. Fails safe: an absent `metadata` or an omitted `preAppDeal` locks the
 * card (only an explicit `preAppDeal: false` releases it), as does a degraded read.
 * @param metadata `additionalProducts.metadata`.
 * @param readOnly Whether the Vehicle tab as a whole is read-only.
 * @returns {boolean} Whether the card may be edited at all.
 */
function isUnlocked(metadata: AdditionalProductsMetadataV2 | undefined, readOnly: boolean): boolean {
  return !readOnly && isFlagsTrusted(metadata);
}

/**
 * Per-field VSC editability from spec 6.2/6.3. Every rule also requires the tab not read-only; the BFF's
 * `flagsUnreliable` / `gatingDegraded` keeps the whole card locked because a `false` flag may only mean the read failed.
 * @param metadata `additionalProducts.metadata`.
 * @param readOnly Whether the Vehicle tab as a whole is read-only (role, VLA lock, status).
 * @returns {VscAccess} Resolved access.
 */
export function resolveVscAccess(metadata: AdditionalProductsMetadataV2 | undefined, readOnly: boolean): VscAccess {
  const tpap = metadata?.tpapVsc === true;
  const base = isUnlocked(metadata, readOnly);
  const enabled = metadata?.vscEnable === true;
  const access = {
    headerLink: tpap && !readOnly,
    vendorDropdown: tpap && isFlagsTrusted(metadata) && metadata?.isDealSubmitted === true,
    vendorEnabled: base,
    certificate: base && enabled && !(metadata?.ecDeal === true && !tpap),
    sellingPrice: base && tpap,
    terms: base && enabled,
    unlimited: base,
    effectiveDate: base && tpap,
    formNumber: base,
  };
  return {
    ...access,
    anyEditable:
      access.vendorEnabled ||
      access.certificate ||
      access.sellingPrice ||
      access.terms ||
      access.unlimited ||
      access.effectiveDate ||
      access.formNumber,
  };
}

/**
 * Per-field GAP editability from spec 7.2. Non-TPAP: prefix additionally needs no third-party GAP
 * provider and both parts need a non-eContract deal; TPAP: only `gapEnable`.
 * @param metadata `additionalProducts.metadata`.
 * @param readOnly Whether the Vehicle tab as a whole is read-only.
 * @returns {GapAccess} Resolved access.
 */
export function resolveGapAccess(metadata: AdditionalProductsMetadataV2 | undefined, readOnly: boolean): GapAccess {
  const tpap = metadata?.tpapGap === true;
  const base = isUnlocked(metadata, readOnly);
  const enabled = base && metadata?.gapEnable === true;
  const nonTpapAddendum = enabled && metadata?.ecDeal !== true;
  const access = {
    headerLink: tpap && !readOnly,
    vendorDropdown: tpap && isFlagsTrusted(metadata) && metadata?.isDealSubmitted === true,
    vendorEnabled: base,
    addendumPrefix: tpap ? enabled : nonTpapAddendum && metadata?.thirdPartyEligible !== true,
    addendumNumber: tpap ? enabled : nonTpapAddendum,
    sellingPrice: base && tpap,
    term: base && tpap,
    amountFinanced: base && tpap,
    effectiveDate: base && tpap,
    formNumber: base && tpap,
  };
  return {
    ...access,
    anyEditable:
      access.vendorEnabled ||
      access.addendumPrefix ||
      access.addendumNumber ||
      access.sellingPrice ||
      access.term ||
      access.amountFinanced ||
      access.effectiveDate ||
      access.formNumber,
  };
}
